import { zodResolver } from '@hookform/resolvers/zod';
import type { CreateInvoiceRequestPayload, CreateInvoiceResponse } from '@vy/api-definition';
import { CreateInvoiceResponseSchema } from '@vy/api-definition';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import type { UseMutationOptions } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { ErrorLabel } from '../../../../../components/design/ErrorLabel';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { getPrivateApi } from '../../../../../services/api';
import { runMutation } from '../../../../../utils/vy-mutation';
import {
  invalidateInvoicesQueries,
  invalidateCustomersQueries,
} from '../../../../../utils/query-invalidation';
import { getDefaultValuesOfInvoiceForm } from './create-invoice-form-consts/create-invoice-default-values';
import { CreateInvoiceProvider } from '../../create-invoice-page-contexts/create-invoice-context/CreateInvoiceProvider';
import type { InvoiceCreationStep } from '../../create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/invoice-creation-step';
import type { InvoiceFormInput } from './create-invoice-form-types/invoice-form-input';
import type { NewInvoiceForm } from './create-invoice-form-types/new-invoice-form';
import { NewInvoiceFormSchema } from './create-invoice-form-types/new-invoice-form';

export function useCreateInvoice(
  options?: UseMutationOptions<CreateInvoiceResponse, unknown, CreateInvoiceRequestPayload>,
) {
  return useMutation(async (newInvoice: CreateInvoiceRequestPayload) => {
    const api = getPrivateApi();
    const createInvoiceResponse = await api.createInvoice({
      createInvoiceRequest: newInvoice,
    });

    return CreateInvoiceResponseSchema.parse(createInvoiceResponse);
  }, options);
}

export function withCreateInvoiceForm(Component: React.FC) {
  return ({ defaultInvoiceCreationStep }: { defaultInvoiceCreationStep: InvoiceCreationStep }) => {
    const navigate = useVyNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const customerIdFromParams = searchParams.get('customerId') ?? undefined;

    useEffect(() => {
      localStorage.removeItem(LocalStorageKeys.CreateInvoice);
    }, [customerIdFromParams]);

    const createInvoiceMutation = useCreateInvoice();

    const methods = useForm<InvoiceFormInput, NewInvoiceForm>({
      resolver: zodResolver(NewInvoiceFormSchema),
      defaultValues: getDefaultValuesOfInvoiceForm(customerIdFromParams),
    });

    useFormPersist(LocalStorageKeys.CreateInvoice, {
      watch: methods.watch,
      setValue: methods.setValue,
      storage: window.localStorage,
    });

    const onSubmit = methods.handleSubmit(async (data) => {
      const validatedInvoiceInput = NewInvoiceFormSchema.parse(data);

      if (isNotNullOrUndefined(validatedInvoiceInput) && !createInvoiceMutation.isLoading) {
        await runMutation(
          createInvoiceMutation,
          validatedInvoiceInput,
          {
            pending: 'Saving...',
            success: 'Saved',
            error: 'Failed to save invoice',
          },
          {
            onSuccess: async ({ invoiceId, customerId }) => {
              await Promise.all([
                invalidateInvoicesQueries(queryClient),
                invalidateCustomersQueries(queryClient),
              ]);
              localStorage.removeItem(LocalStorageKeys.CreateInvoice);
              navigate(`/invoices/${invoiceId}/customer/${customerId}/dynamic`);
            },
          },
        );
      }
    });

    const errorRoot = methods.formState.errors.root;

    return (
      <FormProvider {...methods}>
        <CreateInvoiceProvider defaultInvoiceCreationStep={defaultInvoiceCreationStep} onFinalStep={onSubmit}>
          <Component />
        </CreateInvoiceProvider>

        {isNotNullOrUndefined(errorRoot) && <ErrorLabel errorMessage={errorRoot?.message ?? ''} />}
      </FormProvider>
    );
  };
}
